import { useEffect, useRef, useState } from "react";
import { vehicleState } from "./VehicleFleetList";

type Vehicle = any;

function formatEpoch(ms?: number) {
  if (!ms || typeof ms !== "number") return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

// (no easing needed for speed bar; we use a linear ramp over the 3s interval)

export default function VehicleDetailsSidebar({
  vehicle,
  heroImageUrl,
  onBack,
}: {
  vehicle: Vehicle;
  onBack?: () => void;
  heroImageUrl?: string | null;
}) {
  const isOnline = !vehicle.isEngineOff;
  // Match map smoothing: samples arrive ~every 3s, and display is offset ~6s behind
  // so we can always interpolate between "prev" and "next".
  const DISPLAY_OFFSET_MS = 6000;

  const incomingSpeed = isOnline ? Math.max(0, Number(vehicle.speed || 0)) : 0;
  const [displaySpeed, setDisplaySpeed] = useState<number>(incomingSpeed);
  const rafRef = useRef<number | null>(null);
  const samplesRef = useRef<Array<{ t: number; speed: number }>>([]);
  const vehicleKeyRef = useRef<string | null>(null);
  const rawDriver = (vehicle.driverName || vehicle.addressBookEntry || "").trim();
  const driverName =
    !rawDriver || rawDriver.toLowerCase() === "unknown location" ? "Unknown Driver" : rawDriver;

  // Reset sample buffer when switching vehicles.
  useEffect(() => {
    const key = String(vehicle?._id || vehicle?.vehicleId || "");
    if (vehicleKeyRef.current !== key) {
      vehicleKeyRef.current = key;
      const now = performance.now();
      samplesRef.current = [{ t: now, speed: incomingSpeed }];
      setDisplaySpeed(incomingSpeed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?._id, vehicle?.vehicleId]);

  // Record each incoming speed as a new sample (every ~3s).
  useEffect(() => {
    const now = performance.now();
    const samples = samplesRef.current;
    const last = samples[samples.length - 1];
    if (!last || last.speed !== incomingSpeed) {
      samples.push({ t: now, speed: incomingSpeed });
      // Keep buffer small
      if (samples.length > 10) samples.shift();
    }
  }, [incomingSpeed]);

  // Playback loop: compute speed at displayTime (now - DISPLAY_OFFSET_MS) by interpolating
  // between the surrounding samples across the full 3 seconds.
  useEffect(() => {
    const tick = () => {
      const now = performance.now();
      const displayTime = now - DISPLAY_OFFSET_MS;
      const samples = samplesRef.current;

      let nextSpeed = samples.length ? samples[0].speed : incomingSpeed;

      if (samples.length === 1) {
        nextSpeed = samples[0].speed;
      } else if (samples.length >= 2) {
        // Find the segment containing displayTime
        let i = 0;
        while (i < samples.length - 2 && samples[i + 1].t < displayTime) i++;

        const a = samples[i];
        const b = samples[i + 1];

        if (displayTime <= a.t) {
          nextSpeed = a.speed;
        } else if (displayTime >= b.t) {
          nextSpeed = b.speed;
        } else {
          const dt = Math.max(1, b.t - a.t);
          const p = (displayTime - a.t) / dt; // 0..1 over ~3s
          // Linear ramp like the example 10 -> 11 -> 12 -> 13 across the interval
          nextSpeed = a.speed + (b.speed - a.speed) * p;
        }
      }

      setDisplaySpeed(nextSpeed);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // We intentionally do not depend on incomingSpeed so the RAF loop is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speedMph = isOnline ? Math.floor(displaySpeed) : 0;
  const speedPercentage = isOnline ? Math.min((Math.max(0, displaySpeed) / 80) * 100, 100) : 0;
  return (
    <div className="vehicle-details-sidebar fleet-detail-original">
      {onBack && <button className="fleet-back" type="button" onClick={onBack}><span aria-hidden="true">←</span> All vehicles</button>}
      <div className="fleet-profile">
        <div className="fleet-profile-top">
          <span className={`fleet-state ${isOnline && vehicle.isMoving ? 'moving' : ''}`}><i aria-hidden="true" />{vehicleState(vehicle)}</span>
          <span className="fleet-profile-driver">{driverName}</span>
        </div>
        {heroImageUrl && <img className="fleet-profile-image" src={heroImageUrl} alt="Patrol vehicle reference" />}
        <div className="fleet-profile-heading">
          <h2>{vehicle.displayName || vehicle.vehicleId}</h2>
          <div className="fleet-profile-speed" aria-label="Vehicle speed"><strong>{isOnline ? speedMph : '—'}</strong><span>mph</span></div>
        </div>
        <div className="fleet-profile-meter" aria-hidden="true"><div style={{width: `${speedPercentage}%`}} /></div>
        <div className="fleet-profile-location">
          <svg width="16" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></svg>
          <p>{vehicle.samsaraAddress || 'Unknown Location'}</p>
        </div>
        <div className="fleet-profile-updated"><span>Last movement</span><time>{formatEpoch(vehicle.lastMovementTime)}</time></div>
      </div>
    </div>
  );
}
